import { DateTime } from "luxon";
import React, { useState, useEffect } from "react";
import { formatDateTimeWithOffset } from "../core/date-formatting";
import Typography from "@mui/material/Typography";

const resolveLocale = (maybeLocale) => {
  if (!maybeLocale) {
    return undefined;
  }

  try {
    const [locale] = Intl.DateTimeFormat.supportedLocalesOf([maybeLocale]);
    return locale || undefined;
  } catch {
    return undefined;
  }
};

const formatCurrentTime = (timezone, formattingTimezone) => {
  try {
    const locale = resolveLocale(formattingTimezone);
    const formatter = new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
      timeZone: timezone,
    });

    return formatter.format(new Date());
  } catch {
    return formatDateTimeWithOffset(DateTime.now().setZone(timezone).toMillis(), timezone);
  }
};

/**
 * @param {object} props
 * @param {string} props.timezone
 * @param {"inherit" | "body1" | "body2" | "caption" | "button" | "overline" | "subtitle1" | "subtitle2"} [props.variant]
 * @param {"span" | "p" | "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6"} [props.component]
 * @param {string} [props.formattingTimezone]
 */
export const Now = ({ timezone, variant = "inherit", component = "p", formattingTimezone }) => {
  const [currentTime, setCurrentTime] = useState(formatCurrentTime(timezone, formattingTimezone));

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(formatCurrentTime(timezone, formattingTimezone));
    }, 30000);

    return () => clearInterval(intervalId);
  }, [timezone, formattingTimezone]);

  return (
    <Typography variant={variant} component={component}>
      {currentTime}
    </Typography>
  );
};
